import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { format } from "date-fns";
import { Baby, Search, Eye, FileText, Calendar, AlertTriangle, CheckCircle, Clock } from "lucide-react";
import { PNCCCarePlanSheet } from "./PNCCCarePlanSheet";
import { TimeLogDialog } from "./TimeLogDialog";

interface PrenatalIntake {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  due_date: string | null;
  medicaid_id: string | null;
  phone: string;
  status: string;
  is_eligible: boolean | null;
  eligibility_reason: string | null;
  total_risk_factors: number | null;
  created_at: string;
  care_coordinator_id: string | null;
}

export function PNCCCaseList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedIntake, setSelectedIntake] = useState<PrenatalIntake | null>(null);
  const [showCarePlan, setShowCarePlan] = useState(false);
  const [showTimeLog, setShowTimeLog] = useState(false);

  const { data: intakes, isLoading } = useQuery({
    queryKey: ["prenatal-intakes", statusFilter],
    queryFn: async () => {
      let query = supabase
        .from("prenatal_intake_forms")
        .select("*")
        .order("created_at", { ascending: false });

      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as PrenatalIntake[];
    },
  });

  const filteredIntakes = intakes?.filter((intake) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      intake.first_name.toLowerCase().includes(searchLower) ||
      intake.last_name.toLowerCase().includes(searchLower) ||
      intake.medicaid_id?.toLowerCase().includes(searchLower) ||
      intake.phone.includes(searchTerm)
    );
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; icon: React.ReactNode }> = {
      pending: { variant: "secondary", icon: <Clock className="h-3 w-3 mr-1" /> },
      in_review: { variant: "outline", icon: <Eye className="h-3 w-3 mr-1" /> },
      active: { variant: "default", icon: <CheckCircle className="h-3 w-3 mr-1" /> },
      completed: { variant: "default", icon: <CheckCircle className="h-3 w-3 mr-1" /> },
    };
    const config = variants[status] || variants.pending;
    return (
      <Badge variant={config.variant} className="flex items-center w-fit">
        {config.icon}
        {status.replace("_", " ").charAt(0).toUpperCase() + status.slice(1).replace("_", " ")}
      </Badge>
    );
  };

  const getEligibilityBadge = (isEligible: boolean | null, reason: string | null, riskFactors: number | null) => {
    if (isEligible === null) {
      return <Badge variant="outline">Pending Review</Badge>;
    }
    if (isEligible) {
      return (
        <Badge variant="default" className="bg-primary">
          <CheckCircle className="h-3 w-3 mr-1" />
          Eligible ({reason === "under_18" ? "Under 18" : `${riskFactors} risk factors`})
        </Badge>
      );
    }
    return (
      <Badge variant="destructive">
        <AlertTriangle className="h-3 w-3 mr-1" />
        Not Eligible ({riskFactors} risk factors)
      </Badge>
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Baby className="h-6 w-6 text-primary" />
              <div>
                <CardTitle>PNCC Cases</CardTitle>
                <CardDescription>Prenatal Care Coordination intake forms and cases</CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex gap-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, Medicaid ID, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_review">In Review</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading cases...</div>
          ) : filteredIntakes?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No prenatal intake forms found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Medicaid ID</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Eligibility</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredIntakes?.map((intake) => (
                  <TableRow key={intake.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {intake.first_name} {intake.last_name}
                        </p>
                        <p className="text-sm text-muted-foreground">{intake.phone}</p>
                      </div>
                    </TableCell>
                    <TableCell>{intake.medicaid_id || "—"}</TableCell>
                    <TableCell>
                      {intake.due_date ? format(new Date(intake.due_date), "MMM d, yyyy") : "—"}
                    </TableCell>
                    <TableCell>
                      {getEligibilityBadge(intake.is_eligible, intake.eligibility_reason, intake.total_risk_factors)}
                    </TableCell>
                    <TableCell>{getStatusBadge(intake.status)}</TableCell>
                    <TableCell>
                      {format(new Date(intake.created_at), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedIntake(intake);
                            setShowCarePlan(true);
                          }}
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          Care Plan
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedIntake(intake);
                            setShowTimeLog(true);
                          }}
                        >
                          <Calendar className="h-4 w-4 mr-1" />
                          Time Log
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Care Plan Sheet */}
      {selectedIntake && (
        <PNCCCarePlanSheet
          open={showCarePlan}
          onClose={() => {
            setShowCarePlan(false);
            setSelectedIntake(null);
          }}
          intake={selectedIntake}
        />
      )}

      {/* Time Log Dialog */}
      {selectedIntake && (
        <TimeLogDialog
          open={showTimeLog}
          onClose={() => {
            setShowTimeLog(false);
            setSelectedIntake(null);
          }}
          intake={selectedIntake}
        />
      )}
    </>
  );
}
